import {ModalOverlay, ModalXCloseButton} from '@/components/ui/modal-atoms/ModalAtoms.tsx'
import {
    StyledNotificationsModal,
    StyledNotificationsModalBody,
    StyledNotificationsModalHeader,
    StyledNotificationsModalHeaderWrapper,
    StyledNotificationsModalSubheader,
    StyledNotificationsModalTitle
} from '@/features/notifications/components/notifications-modal/style.ts'
import {Flexbox} from '@/components/ui/flexbox/FlexBox.tsx'
import {memo, useEffect, useState} from 'react'
import {NotificationsContent} from '@/features/notifications/components/notifications-content/NotificationsContent'
import {Text} from '@/components/ui/text/Text'
import {Button} from '@/components/ui/button/Button'
import {useTranslation} from '@/translations/i18n'
import {useGetInfiniteNotifications} from '@/features/notifications/queries/useGetInfiniteNotifications'
import {useGetNotificationCount} from '@/features/notifications/queries/useGetNotificationCount'
import {useMarkAllNotificationsAsRead} from '@/features/notifications/queries/useMarkAllNotificationsAsRead'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'

interface NotificationsModalProps {
    isOpen: boolean
    onClose: () => void
}

const NotificationsModalComponent = ({isOpen, onClose}: NotificationsModalProps) => {
    const {t} = useTranslation()
    const [showOnlyUnread, setShowOnlyUnread] = useState<boolean | undefined>(true)

    // Fetch notifications using the infinite query hook
    const {notifications, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage} = useGetInfiniteNotifications(
        showOnlyUnread,
        30,
        {
            enabled: isOpen
        }
    )

    // Get notification count
    const {data: countData} = useGetNotificationCount()
    const unreadCount = countData?.toRead || 0

    // Mark all as read mutation
    const {mutate: markAllAsRead, isPending: isMarkingAllAsRead} = useMarkAllNotificationsAsRead()

    // Show all notifications by default when modal opens
    useEffect(() => {
        if (isOpen) {
            setShowOnlyUnread(true)
        }
    }, [isOpen])

    if (!isOpen) return null

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <StyledNotificationsModal width="500px" onClick={event => event.stopPropagation()}>
                <StyledNotificationsModalHeaderWrapper>
                    <StyledNotificationsModalHeader>
                        <Flexbox justify="space-between" align="center">
                            <StyledNotificationsModalTitle>{t('notifications:title')}</StyledNotificationsModalTitle>
                            <ModalXCloseButton onClick={onClose} />
                        </Flexbox>
                    </StyledNotificationsModalHeader>
                    <StyledNotificationsModalSubheader align="center" direction="row" justify="space-between">
                        <Flexbox align="center" gap={2}>
                            <Text
                                size="textSm"
                                palette={showOnlyUnread === true ? 'primary' : 'neutral'}
                                shade={showOnlyUnread === true ? '600' : '500'}
                                onClick={() => setShowOnlyUnread(true)}
                                style={{cursor: 'pointer'}}
                            >
                                {t('notifications:unread', {count: unreadCount})}
                            </Text>
                            <Text
                                size="textSm"
                                palette={showOnlyUnread === undefined ? 'primary' : 'neutral'}
                                shade={showOnlyUnread === undefined ? '600' : '500'}
                                onClick={() => setShowOnlyUnread(undefined)}
                                style={{cursor: 'pointer'}}
                            >
                                {t('notifications:all')}
                            </Text>
                        </Flexbox>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAllAsRead()}
                            disabled={unreadCount === 0 || isMarkingAllAsRead}
                        >
                            {t('notifications:mark_all_as_read')}
                        </Button>
                    </StyledNotificationsModalSubheader>
                </StyledNotificationsModalHeaderWrapper>
                <StyledNotificationsModalBody>
                    <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />}>
                        <NotificationsContent
                            notifications={notifications}
                            isLoading={isLoading}
                            hasMorePages={hasNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                            onLoadMore={() => fetchNextPage()}
                            onNotificationClick={onClose}
                        />
                    </ScrollArea>
                </StyledNotificationsModalBody>
            </StyledNotificationsModal>
        </>
    )
}

export const NotificationsModal = memo(NotificationsModalComponent)
